import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';
import { fromEvent } from 'rxjs';
import { AdminIconComponent } from './admin-icon.component';
import { CrmSearchArea, CrmSearchResultItem, CrmSearchService } from '../services/crm-search.service';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

@Component({
  selector: 'app-admin-crm-global-search',
  standalone: true,
  imports: [FormsModule, AdminIconComponent],
  template: `
    <div class="cg-admin-crm-search" #searchRoot>
      <label class="cg-admin-crm-search__field">
        <span class="cg-admin-crm-search__icon" aria-hidden="true">
          <app-admin-icon name="search" [size]="18" />
        </span>
        <input
          class="cg-admin-crm-search__input cg-input"
          type="search"
          name="crmGlobalSearch"
          autocomplete="off"
          aria-label="Search leads and applicants"
          [attr.aria-expanded]="panelOpen()"
          aria-controls="crm-global-search-results"
          aria-autocomplete="list"
          placeholder="Search leads & applicants…"
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange()"
          (focus)="onFocus()"
          (keydown.escape)="closePanel()"
        />
      </label>

      @if (panelOpen()) {
        <div
          id="crm-global-search-results"
          class="cg-admin-crm-search__panel"
          role="listbox"
          aria-label="Search results"
        >
          @if (loading()) {
            <p class="cg-admin-crm-search__status">Searching…</p>
          } @else if (showNoResults()) {
            <p class="cg-admin-crm-search__status cg-admin-crm-search__status--empty">No Results Found</p>
          } @else {
            <ul class="cg-admin-crm-search__list">
              @for (item of results(); track item.id) {
                <li>
                  <button
                    type="button"
                    class="cg-admin-crm-search__item"
                    role="option"
                    (click)="selectResult(item)"
                  >
                    <div class="cg-admin-crm-search__item-main">
                      <span class="cg-admin-crm-search__name">{{ item.fullName }}</span>
                      <span
                        class="cg-admin-crm-search__type"
                        [class.cg-admin-crm-search__type--applicant]="item.recordType === 'applicant'"
                      >
                        {{ recordTypeLabel(item.recordType) }}
                      </span>
                    </div>
                    <div class="cg-admin-crm-search__meta">
                      @if (item.email) {
                        <span>{{ item.email }}</span>
                      }
                      @if (item.phoneNumber) {
                        <span>{{ item.phoneNumber }}</span>
                      }
                    </div>
                    @if (item.statusName) {
                      <span class="cg-admin-crm-search__status-badge">{{ item.statusName }}</span>
                    }
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  `,
})
export class AdminCrmGlobalSearchComponent {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly crmSearch = inject(CrmSearchService);
  private readonly searchRoot = viewChild<ElementRef<HTMLElement>>('searchRoot');
  private readonly query$ = new Subject<string>();

  query = '';
  readonly results = signal<CrmSearchResultItem[]>([]);
  readonly loading = signal(false);
  readonly panelOpen = signal(false);
  readonly hasSearched = signal(false);

  readonly searchArea = signal<CrmSearchArea>('platform');

  readonly showNoResults = computed(
    () => this.hasSearched() && !this.loading() && this.results().length === 0 && this.query.trim().length >= MIN_QUERY_LENGTH,
  );

  constructor() {
    this.syncSearchArea(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.syncSearchArea(event.urlAfterRedirects);
        this.reset();
      });

    this.query$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap((value) => {
          const trimmed = value.trim();
          if (trimmed.length < MIN_QUERY_LENGTH) {
            this.results.set([]);
            this.hasSearched.set(false);
            this.loading.set(false);
            return;
          }
          this.loading.set(true);
          this.hasSearched.set(true);
          this.panelOpen.set(true);
        }),
        switchMap((value) => {
          const trimmed = value.trim();
          if (trimmed.length < MIN_QUERY_LENGTH) {
            return of<CrmSearchResultItem[]>([]);
          }
          return this.crmSearch.search(this.searchArea(), trimmed).pipe(
            catchError(() => of<CrmSearchResultItem[]>([])),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((items) => {
        this.results.set(items);
        this.loading.set(false);
      });

    fromEvent<MouseEvent>(this.doc, 'click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const root = this.searchRoot()?.nativeElement;
        if (root && !root.contains(event.target as Node)) {
          this.closePanel();
        }
      });
  }

  onQueryChange(): void {
    this.query$.next(this.query);
    if (this.query.trim().length >= MIN_QUERY_LENGTH) {
      this.panelOpen.set(true);
    }
  }

  onFocus(): void {
    if (this.query.trim().length >= MIN_QUERY_LENGTH) {
      this.panelOpen.set(true);
    }
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  selectResult(item: CrmSearchResultItem): void {
    const base = this.searchArea() === 'tenant' ? '/tenant' : '/platform';
    const segment = item.recordType === 'applicant' ? 'applicants' : 'leads';
    void this.router.navigate([`${base}/${segment}`, item.id]);
    this.reset();
  }

  recordTypeLabel(recordType: CrmSearchResultItem['recordType']): string {
    return recordType === 'applicant' ? 'Applicant' : 'Lead';
  }

  private syncSearchArea(url: string): void {
    this.searchArea.set(url.startsWith('/tenant') ? 'tenant' : 'platform');
  }

  private reset(): void {
    this.query = '';
    this.results.set([]);
    this.loading.set(false);
    this.hasSearched.set(false);
    this.panelOpen.set(false);
  }
}
